import {
  KnownHistoricalUtilisationTurnoverMetadataGroupBy,
  KnownHubStatus
} from '@groundfloor-platform/mgmt-service-g-sdk';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useImmer } from 'use-immer';
import { useCurrentProject } from '../../../core/context/project.context';
import { useQueryListHubs } from '../../lockers/hubs.queries';
import { getDateRange, DateRange } from '../_shared/date-range';
import {
  useQueryDeliveryContainerCapacity,
  useQueryDeliveryContainerUtilisation
} from './delivery-container-utilisation-report.queries';

/**
 * Backend analytics never include "today" in either chart's data, so trim the upper bound
 * to end-of-yesterday. Without this, "today" appears as an empty column in the utilisation
 * heatmap and the capacity chart pads out to "now" with no bars.
 */
function getDeliveryContainerUtilisationDateRange(range: DateRange) {
  const { fromDate, toDate } = getDateRange(range);
  return { fromDate, toDate: dayjs(toDate).subtract(1, 'day').endOf('day').toDate() };
}

type State = {
  utilisationQuery: {
    groupBy: KnownHistoricalUtilisationTurnoverMetadataGroupBy;
    fromDate?: Date;
    toDate: Date;
    hubID?: string;
  };
  range: DateRange;
};

export function useDeliveryContainerUtilisationReport(projectID: string) {
  const [state, setState] = useImmer<State>({
    utilisationQuery: {
      ...getDeliveryContainerUtilisationDateRange(DateRange.Week),
      groupBy: KnownHistoricalUtilisationTurnoverMetadataGroupBy.Hub
    },
    range: DateRange.Week
  });

  // Pull the timezone from the project context so the backend
  // aligns daily report buckets to the project's local day rather
  // than UTC. Without this the turnover chart's "Mon 1 Jun" bar
  // spans UTC Mon → Tue (10 hours offset from Melbourne days) and
  // disagrees with the peak-bands chart on day-of-week. When the
  // project hasn't yet loaded `timezone` is undefined and the
  // backend falls back to its own `Australia/Melbourne` default.
  const { project } = useCurrentProject();
  const timeZone = project?.timezone;

  const utilisationQuery = useMemo(() => ({ ...state.utilisationQuery, timeZone }), [state.utilisationQuery, timeZone]);

  const hubData = useQueryListHubs(projectID, {
    includeAppearsOn: true,
    includeChildProjects: true,
    status: KnownHubStatus.Active
  });
  const utilisation = useQueryDeliveryContainerUtilisation(projectID, utilisationQuery);
  const capacity = useQueryDeliveryContainerCapacity(projectID, utilisationQuery);

  const hubOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [
      { label: 'All related hubs', value: 'all' },
      ...(hubData.data || []).map((hub) => ({ label: hub.name, value: hub.id }))
    ];
    return options;
  }, [hubData.data]);

  useEffect(() => {
    const currentHub = state.utilisationQuery.hubID;
    if (!currentHub || currentHub === 'all' || !hubData.data || hubData.isLoading) {
      return;
    }
    if (!hubData.data.map((hub) => hub.id).includes(currentHub)) {
      setState((draft) => {
        draft.utilisationQuery.hubID = undefined;
      });
    }
  }, [hubData.data, hubData.isLoading, setState, state.utilisationQuery.hubID]);

  const dateRangeOptions: { label: string; value: DateRange }[] = [
    { value: DateRange.Week, label: '1 week' },
    { value: DateRange.Month, label: '1 month' },
    { value: DateRange.HalfYear, label: '6 months' },
    { value: DateRange.Year, label: '1 year' }
  ];

  const groupByOptions: { label: string; value: KnownHistoricalUtilisationTurnoverMetadataGroupBy }[] = [
    { value: KnownHistoricalUtilisationTurnoverMetadataGroupBy.Hub, label: 'Grouped by hub' },
    { value: KnownHistoricalUtilisationTurnoverMetadataGroupBy.Size, label: 'Grouped by size' },
    { value: KnownHistoricalUtilisationTurnoverMetadataGroupBy.None, label: 'No grouping' }
  ];

  function onSelectHub(selection: string) {
    const selectedHubID = selection === 'all' ? undefined : selection;
    setState((draft) => {
      draft.utilisationQuery.hubID = selectedHubID;
    });
  }

  function onSelectDateRange(selection: DateRange) {
    setState((draft) => {
      const { fromDate, toDate } = getDeliveryContainerUtilisationDateRange(selection);
      draft.utilisationQuery.fromDate = fromDate;
      draft.utilisationQuery.toDate = toDate;
      draft.range = selection;
    });
  }

  function onSelectGroupBy(groupBy: KnownHistoricalUtilisationTurnoverMetadataGroupBy) {
    setState((draft) => {
      draft.utilisationQuery.groupBy = groupBy;
    });
  }

  return {
    state,
    data: { utilisation, capacity },
    options: { hubOptions, dateRangeOptions, groupByOptions },
    handlers: { onSelectDateRange, onSelectGroupBy, onSelectHub }
  } as const;
}
